import React from 'react';
import Icon from '../../../components/AppIcon';

const OrderTrackingInfo = ({ orderNumber, trackingSteps }) => {
  const defaultTrackingSteps = [
    {
      id: 1,
      title: "Order Confirmed",
      description: "Your order has been received and confirmed",
      status: "completed",
      timestamp: "2025-01-20 10:30 AM"
    },
    {
      id: 2,
      title: "Processing",
      description: "Your order is being prepared for shipment",
      status: "current",
      timestamp: null
    },
    {
      id: 3,
      title: "Shipped",
      description: "Your order has been shipped and is on its way",
      status: "upcoming",
      timestamp: null
    },
    {
      id: 4,
      title: "Delivered",
      description: "Your order has been delivered successfully",
      status: "upcoming",
      timestamp: null
    }
  ];

  const steps = trackingSteps || defaultTrackingSteps;

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'current':
        return 'Clock';
      case 'upcoming':
        return 'Circle';
      default:
        return 'Circle';
    }
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success';
      case 'current':
        return 'text-primary';
      case 'upcoming':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
        <Icon name="Package" size={24} className="mr-2 text-primary" />
        Order Tracking
      </h2>
      
      <div className="mb-4 p-4 bg-muted rounded-lg">
        <p className="text-sm text-muted-foreground mb-1">Tracking Number</p>
        <p className="font-mono text-foreground font-medium">TRK{orderNumber}2025</p>
      </div>
      
      {/* Tracking Timeline */}
      <div className="space-y-4">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          
          return (
            <div key={step.id} className="flex items-start space-x-4">
              {/* Step Icon */}
              <div className="flex flex-col items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors
                  ${step.status === 'completed' 
                    ? 'bg-success border-success text-white' 
                    : step.status === 'current' ?'bg-primary border-primary text-white' :'bg-background border-border text-muted-foreground'
                  }
                `}>
                  <Icon 
                    name={getStepIcon(step.status)} 
                    size={16} 
                    color="currentColor"
                  />
                </div>
                
                {/* Connector Line */}
                {!isLast && (
                  <div className={`w-0.5 h-8 mt-2 transition-colors ${
                    step.status === 'completed' ? 'bg-success' : 'bg-border'
                  }`} />
                )}
              </div>
              
              {/* Step Content */}
              <div className="flex-1 pb-8">
                <div className="flex items-center justify-between mb-1">
                  <h3 className={`font-medium ${getStepColor(step.status)}`}>
                    {step.title}
                  </h3>
                  {step.timestamp && (
                    <span className="text-xs text-muted-foreground">
                      {step.timestamp}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {step.description}
                </p>
                
                {step.status === 'current' && (
                  <div className="mt-2 flex items-center text-xs text-primary">
                    <Icon name="Clock" size={12} className="mr-1" />
                    <span>Estimated completion: 1-2 business days</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Tracking Notice */}
      <div className="mt-6 p-4 bg-background border border-border rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-1">Tracking Updates</p>
            <p className="text-muted-foreground">
              You'll receive email notifications at each step of your order's journey. 
              Tracking information may take 24-48 hours to appear after shipment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingInfo;